import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, MessageCircle } from "lucide-react";

export default function ChatPanel({ contractorTenantId, vendorId, subjectType, subjectId, subjectLabel }) {
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!contractorTenantId || !vendorId || !subjectId) return;
      const { data } = await api.post("/chat/threads", {
        contractor_tenant_id: contractorTenantId,
        vendor_id: vendorId,
        subject_type: subjectType,
        subject_id: subjectId,
        subject_label: subjectLabel,
      });
      if (!mounted) return;
      setThread(data);
      const msgsRes = await api.get(`/chat/threads/${data.id}/messages`);
      if (!mounted) return;
      setMessages(msgsRes.data);
    })();
    return () => {
      mounted = false;
    };
  }, [contractorTenantId, vendorId, subjectType, subjectId, subjectLabel]);

  // Live delivery over the WebSocket is primary — no manual refresh needed.
  // If the socket drops it reconnects with backoff (capped at 10s); if it
  // never manages to connect at all (proxy/firewall blocking upgrades), a
  // 5s poll of the thread's messages keeps the panel live anyway.
  useEffect(() => {
    if (!thread) return;
    let cancelled = false;
    let ws = null;
    let retryTimer = null;
    let pollTimer = null;
    let attempt = 0;
    let everConnected = false;

    const stopPolling = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        api.get(`/chat/threads/${thread.id}/messages`).then((r) => { if (!cancelled) setMessages(r.data); }).catch(() => {});
      }, 5000);
    };

    const connect = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/auth/ws-token");
        if (cancelled) return;
        const wsBase = API.replace(/^http/, "ws");
        ws = new WebSocket(`${wsBase}/chat/ws/${thread.id}?token=${data.token}`);
        wsRef.current = ws;
        ws.onopen = () => { everConnected = true; attempt = 0; stopPolling(); };
        ws.onmessage = (evt) => {
          const msg = JSON.parse(evt.data);
          setMessages((prev) => [...prev, msg]);
        };
        ws.onclose = () => {
          if (cancelled) return;
          wsRef.current = null;
          // Never managed to connect at all — stop retrying the socket and
          // fall back to polling for the life of this panel.
          if (!everConnected && attempt >= 3) { startPolling(); return; }
          attempt += 1;
          const delay = Math.min(1000 * 2 ** attempt, 10000);
          retryTimer = setTimeout(connect, delay);
        };
        ws.onerror = () => ws && ws.close();
      } catch {
        if (!cancelled) startPolling();
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      stopPolling();
      if (ws) ws.close();
      wsRef.current = null;
    };
  }, [thread?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ message: trimmed }));
    } else if (thread) {
      api.post(`/chat/threads/${thread.id}/messages`, { message: trimmed }).then((r) => setMessages((p) => [...p, r.data]));
    }
    setText("");
  };

  return (
    <div className="border border-slate-200 rounded-xl flex flex-col h-[420px]" data-testid="chat-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <MessageCircle size={15} className="text-sky-600" />
        <span className="text-[13px] font-semibold">Chat — {subjectLabel}</span>
      </div>
      <div className="flex-1 overflow-y-auto ss-scrollbar px-4 py-3 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-[12.5px] text-slate-400 text-center py-6">No messages yet. Start the conversation.</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_portal === user?.portal;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3 py-2 rounded-lg text-[12.5px] ${mine ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                <div className="font-semibold text-[11px] opacity-80 mb-0.5">{m.sender_name}</div>
                {m.message}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-slate-100 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message..."
          data-testid="chat-input"
          className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-sky-400"
        />
        <button onClick={send} data-testid="chat-send-button" className="w-9 h-9 rounded-lg bg-sky-500 text-white flex items-center justify-center hover:bg-sky-600">
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
